import { useContext, useEffect, useMemo, useState } from 'react';
import { MessageDTO } from '../../Types/MessageT';
import { EventS } from '../EventS';
import BookingEvent from '../../Types/Event';
import { fetchContactById, getCustomerId } from '../restapi/contactService';
import { fetchBriefestEventParticipationByContactId } from '../restapi/eventService';
import { LocationS } from '../LocationS';
import { LocationDto } from '../../Types/LocationT';
import { ContactContext } from '../Context/ContactContext';
import { fetchFlattenedCustomAttributeValues } from '../restapi/customAttributeService';
import { fetchShares } from '../restapi/shareService';
import { ShareS } from '../ShareS';
import { ContactDetailCtx } from '../Context/ContactDetailsContext';
import { parsePlaceholders } from '../Placeholder/PlaceholderResover';

const getPlaceholderIds = (message: MessageDTO) =>
  Array.from(
    new Set(
      [...parsePlaceholders(message.subject ?? ''), ...parsePlaceholders(message.message ?? '')].map((placeholder) =>
        placeholder.substring(1, placeholder.length - 1),
      ),
    ),
  );

const isCustomerPlaceholder = (placeholderId: string) =>
  placeholderId.startsWith('customer_') ||
  placeholderId === 'customer_and_partner_names' ||
  placeholderId === 'customer_and_partner_first_names';

const isEventPlaceholder = (placeholderId: string) =>
  placeholderId.startsWith('event_') || placeholderId === 'last_event_date';

// eslint-disable-next-line import/prefer-default-export
export const useMessageEntities = (message: MessageDTO) => {
  const [event, setEvent] = useState<BookingEvent | null>(null);
  const [customer, setCustomer] = useState();
  const [location, setLocation] = useState<LocationDto>();
  const [customAttributeValues, setCustomAttributeValues] = useState();
  const [portalLink, setPortalLink] = useState<string>();
  const [resolvedEventId, setResolvedEventId] = useState<number | null>(message.eventId);
  const contactDetailCtx = useContext(ContactDetailCtx);
  const { eventId } = message;

  const { contact } = useContext<any>(ContactContext);
  const { id: contactId } = contact;
  const placeholderIds = useMemo(() => getPlaceholderIds(message), [message.message, message.subject]);
  const requiresCustomer = useMemo(() => placeholderIds.some(isCustomerPlaceholder), [placeholderIds]);
  const requiresLocation = useMemo(
    () => placeholderIds.some((placeholderId) => placeholderId.startsWith('location_')),
    [placeholderIds],
  );
  const requiresCustomAttributeValues = useMemo(
    () => placeholderIds.some((placeholderId) => placeholderId.includes(':')),
    [placeholderIds],
  );
  const requiresPortalLink = useMemo(() => placeholderIds.includes('event_portalLink'), [placeholderIds]);
  const requiresEvent = useMemo(
    () => placeholderIds.some(isEventPlaceholder) || requiresPortalLink,
    [placeholderIds, requiresPortalLink],
  );
  const requiresEventContext = requiresEvent || requiresLocation || requiresCustomAttributeValues;
  const relatedCustomerId = useMemo(
    () => (requiresCustomer || requiresEventContext ? getCustomerId(message) : undefined),
    [message, requiresCustomer, requiresEventContext],
  );

  useEffect(() => {
    let active = true;

    if (!requiresEventContext) {
      setResolvedEventId(null);
      return () => {
        active = false;
      };
    }

    if (eventId) {
      setResolvedEventId(eventId);
      return () => {
        active = false;
      };
    }

    if (!relatedCustomerId) {
      setResolvedEventId(null);
      return () => {
        active = false;
      };
    }

    fetchBriefestEventParticipationByContactId(relatedCustomerId.toString(), true).then((resp) => {
      if (!active || !resp.ok) {
        return;
      }
      resp.json().then((participations) => {
        if (!active) {
          return;
        }

        if (participations.length > 0) {
          console.log(`got ${participations.length} participations for contact ${relatedCustomerId}`);
          setResolvedEventId(participations[0].eventId);
        } else {
          setResolvedEventId(null);
        }
      });
    });

    return () => {
      active = false;
    };
  }, [eventId, relatedCustomerId, requiresEventContext]);

  useEffect(() => {
    let active = true;

    if (!requiresEvent || !resolvedEventId) {
      setEvent(null);
      return () => {
        active = false;
      };
    }

    EventS.fetchById(resolvedEventId).then((fetchedEvent) => {
      if (active) {
        setEvent(fetchedEvent);
      }
    });

    return () => {
      active = false;
    };
  }, [requiresEvent, resolvedEventId]);

  useEffect(() => {
    let active = true;

    if (!requiresLocation || !resolvedEventId) {
      setLocation(undefined);
      return () => {
        active = false;
      };
    }

    LocationS.fetchByEventId(resolvedEventId).then((loc) => {
      if (!active || !loc) {
        return;
      }

      const { id: locationId } = loc;
      if (LocationS.isEmptyLocation(loc)) {
        setLocation(loc);
      } else {
        LocationS.calculateDistance(contactId, locationId).then((_distance) => {
          if (active) {
            setLocation({ ...loc, distance: _distance ? Math.round(_distance.distanceInMeters / 1000) : null });
          }
        });
      }
    });

    return () => {
      active = false;
    };
  }, [contactId, requiresLocation, resolvedEventId]);

  useEffect(() => {
    let active = true;

    if (!requiresCustomer || contactDetailCtx.contact) {
      setCustomer(undefined);
      return () => {
        active = false;
      };
    }

    if (relatedCustomerId) {
      fetchContactById(relatedCustomerId.toString()).then((mainCustomer) => {
        if (active) {
          setCustomer(mainCustomer as any);
        }
      });
      return () => {
        active = false;
      };
    }

    if (event?.customerId) {
      fetchContactById(event.customerId.toString()).then((mainCustomer) => {
        if (active) {
          setCustomer(mainCustomer as any);
        }
      });
    }

    return () => {
      active = false;
    };
  }, [contactDetailCtx.contact, event, relatedCustomerId, requiresCustomer]);

  useEffect(() => {
    let active = true;

    if (!requiresPortalLink || !resolvedEventId) {
      setPortalLink(undefined);
      return () => {
        active = false;
      };
    }

    fetchShares('events', resolvedEventId).then((resp) => {
      if (!active || !resp.ok) {
        return;
      }
      resp.json().then((shares) => {
        if (active) {
          setPortalLink(shares.length > 0 ? ShareS.getPortalLink(shares[0]) : undefined);
        }
      });
    });

    return () => {
      active = false;
    };
  }, [requiresPortalLink, resolvedEventId]);

  useEffect(() => {
    let active = true;

    if (!requiresCustomAttributeValues || !resolvedEventId) {
      setCustomAttributeValues(undefined);
      return () => {
        active = false;
      };
    }

    fetchFlattenedCustomAttributeValues('event', resolvedEventId).then((resp) => {
      if (!active || !resp.ok) {
        return;
      }
      resp.json().then((flattenedValues) => {
        if (active) {
          setCustomAttributeValues(flattenedValues);
        }
      });
    });

    return () => {
      active = false;
    };
  }, [requiresCustomAttributeValues, resolvedEventId]);

  return {
    event: event ? { ...event, portalLink } : undefined,
    customer: contactDetailCtx.contact ? contactDetailCtx.contact : customer,
    eventLocation: location,
    customAttributeValues,
  };
};
